import React, { useLayoutEffect } from 'react';
import { Box, CircularProgress, Typography, Stack, styled, Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PricingSurchargeCalculationType, PricingSurchargeSupplierOption } from '@idearoom/types';
import { Page } from '../library/Page';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { PageHeader } from '../library/PageHeader';
import { PageContentCard } from '../library/PageContentCard';
import { I18nKeys } from '../../constants/I18nKeys';
import { ClientDataBranch } from '../../constants/ClientDataBranch';
import { PricingPublishDialog } from '../PricingPublishDialog';
import { SurchargePreviewDialog } from './SurchargePreviewDialog';
import { PricingPublishResultDialog } from '../PricingPublishResultDialog';
import { setClientDataType, setSelectedTable } from '../../ducks/clientDataSlice';
import { ClientDataType } from '../../constants/ClientDataType';
import { PRICING_SURCHARGE_TABLE } from '../../constants/ClientData';
import { usePricingRepo } from '../../hooks/usePricingRepo';
import { unknownGroup } from '../../constants/Group';
import { viewingDealer } from '../../utils/supplierUtils';
import { SurchargeSupplierSelector } from './SurchargeSupplierSelector';
import { SurchargeTypeSelector } from './SurchargeTypeSelector';
import { SurchargeCalculationSection } from './SurchargeCalculationSection';
import { SurchargeAmountsSection } from './SurchargeAmountsSection';

const Header = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#000000',
  opacity: 0.87,
});

export const DiscountsAndSurcharges: React.FC<{ clientId: string }> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { isCreatingBranch } = useAppSelector((state) => state.clientData);
  const subMenuItem = useAppSelector((state) => state.menu.subMenuItem);
  const isInitializingBranch = useAppSelector((state) => state.clientData.isInitializingBranch);

  const { isInitializingSelectedPricingSurcharge, selectedPricingSurcharge } = usePricingRepo({
    useSelectedPricingSurcharge: true,
  });
  const { key: pricingSelection, supplierSurcharge: pricingSupplierSelection } = selectedPricingSurcharge;

  const { configurators = [] } = useAppSelector((state) => state.currentUser.group || unknownGroup);
  const isDealer = viewingDealer(subMenuItem, configurators);
  const pricingSupplierSelectionOption =
    pricingSupplierSelection ||
    (isDealer ? PricingSurchargeSupplierOption.Inherit : PricingSurchargeSupplierOption.Off);

  useLayoutEffect(() => {
    dispatch(setClientDataType(ClientDataType.Vendor));
    dispatch(setSelectedTable(PRICING_SURCHARGE_TABLE));
  });

  return (
    <Page>
      <PageHeader id="discounts-surcharges-header" header="Discounts & Surcharges" />
      <PageContentCard>
        {(isInitializingSelectedPricingSurcharge || isCreatingBranch || isInitializingBranch) && (
          <Box display="flex" flex={1} justifyContent="center" alignItems="center" height="149px">
            <CircularProgress color="secondary" />
          </Box>
        )}
        {!isInitializingSelectedPricingSurcharge && !isCreatingBranch && !isInitializingBranch && (
          <Stack spacing="24px">
            {isDealer && <SurchargeSupplierSelector />}
            <Stack spacing="13px">
              <Header>
                {isDealer ? t(I18nKeys.PricingChangesSupplierPrefix) : ''}
                {t(I18nKeys.PricingChangesTitle)}
              </Header>
              <Box ml="16px !important">
                <SurchargeTypeSelector
                  dealer={isDealer}
                  disabled={pricingSupplierSelectionOption === PricingSurchargeSupplierOption.Inherit}
                />
              </Box>
            </Stack>
            <Collapse
              in={
                pricingSelection &&
                pricingSelection !== PricingSurchargeCalculationType.None &&
                pricingSupplierSelectionOption !== PricingSurchargeSupplierOption.Inherit
              }
              timeout="auto"
              unmountOnExit
              sx={{ width: '100%' }}
            >
              <Stack spacing="24px">
                <Stack spacing="13px">
                  <Header>{t(I18nKeys.PricingChangesCalculation)}</Header>
                  <Box ml="20px !important">
                    <SurchargeCalculationSection />
                  </Box>
                </Stack>
                <Stack spacing="13px">
                  <Header>{t(I18nKeys.PricingChangesAmounts)}</Header>
                  <Box ml="13px !important">
                    <SurchargeAmountsSection />
                  </Box>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        )}
      </PageContentCard>

      <SurchargePreviewDialog />
      <PricingPublishDialog
        pricingBranch={ClientDataBranch.PricingSurcharge}
        title={I18nKeys.PricingSurchargePublishDialogTitle}
        content={I18nKeys.PricingSurchargePublishDialogText}
      />
      <PricingPublishResultDialog />
    </Page>
  );
};
